import { toast } from 'sonner';
import { Download, EllipsisVertical, Star } from 'lucide-react';
import { BiCommentDetail } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  IAttachmentCategory,
  IPostAttachment,
  IPostCategory,
  IPostVisibility,
} from '@/redux/post/post.d';
import { cn, getRelativeTime } from '@/lib/utils';
import ProfilePicture from '../ui/profile-picture';
import LikeButton from '@/redux/like/LikeButton';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeletePostDeleteMutation } from '@/redux/post/post.api.slice';
import PostStatusBadge from './PostStatusBadge';
import { IPartnerData } from '@/redux/partner/partner';

interface IPostCardProps {
  id: string;
  authorName: string;
  authorAvatar: string;
  title: string;
  description: string;
  likesCount: number;
  commentsCount: number;
  attachments: IPostAttachment[];
  createdAt: string;
  category: IPostCategory;
  hasLikedByUser: boolean | undefined;
  isOnefinityOfficial: boolean;
  isOwner: boolean;
  visibility?: IPostVisibility;
  authorUsername: string;
  partnerData?: IPartnerData;
}

function PostCard({
  id,
  authorName,
  authorAvatar,
  title,
  description,
  likesCount,
  commentsCount,
  attachments,
  createdAt,
  category,
  hasLikedByUser,
  isOnefinityOfficial,
  isOwner,
  visibility,
  authorUsername,
  partnerData,
}: IPostCardProps) {
  const navigate = useNavigate();

  const [deletePost] = useDeletePostDeleteMutation();
  const thumbnail = attachments.filter((attachment) => {
    if (attachment.category !== IAttachmentCategory.postThumbnail) return false;

    return true;
  });

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!postToDelete) return;

    try {
      await deletePost(postToDelete).unwrap();
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete the project. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const confirmDelete = (postId: string) => {
    setPostToDelete(postId);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (postId: string) => {
    navigate(`/project/edit/${postId}?page=1&edit=true`);
  };

  const handleDialogClose = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
  };

  const totalAttachmentsDownloadCount = attachments.reduce(
    (count, attachment) => count + (attachment.downloadCount || 0),
    0
  );
  const totalDownloadCount = Math.round(
    totalAttachmentsDownloadCount / attachments.length
  );

  return (
    <>
      <Card className="w-[342px] h-[478px] flex flex-col bg-white border-gradient border-[1.5px]">
        <div className="flex justify-between flex-row items-center p-3">
          <Link to={`/${authorUsername}`}>
            <div className="flex gap-3 items-center">
              <ProfilePicture src={authorAvatar} alt={authorName} />
              <div className="flex flex-col">
                <div className="flex gap-2 items-center">
                  <h6 className="font-primary font-medium">
                    @{authorUsername}
                  </h6>
                  <div className="flex gap-2 items-center">
                    {isOnefinityOfficial ? (
                      <img
                        src={OnefinityOfficialIcon}
                        className="h-6 w-6"
                        alt="onefinity official"
                      />
                    ) : (
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <></>
                    )}
                    {partnerData ? (
                      <div
                        className={cn(
                          `h-6 w-6 rounded-full flex items-center justify-center`
                        )}
                        style={{
                          backgroundColor: partnerData?.color,
                        }}
                      >
                        <Star className="h-3 w-3 text-white" fill="white" />
                      </div>
                    ) : (
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <></>
                    )}
                  </div>
                </div>
                <p className="font-secondary text-[#666666]">
                  {getRelativeTime(createdAt)}
                </p>
              </div>
            </div>
          </Link>
          {isOwner ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleEdit(id)}>
                  Edit Project
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => confirmDelete(id)}
                  className="focus:text-red-500 focus:bg-red-200"
                >
                  Delete Project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          )}
        </div>
        <Link to={`/model/${id}`} className="h-full">
          <img
            src={thumbnail[0]?.url}
            alt="avatar"
            className="h-[192px] w-full object-contain"
          />
          <div className="flex flex-col justify-between p-5">
            <div className="flex flex-col justify-between gap-[16px]">
              <div className="flex flex-col gap-[8px]">
                <div className="flex justify-between items-center">
                  <h5 className="text-lg font-medium leading-none font-primary capitalize truncate">
                    {title}
                  </h5>
                  {visibility ? (
                    <PostStatusBadge
                      visibility={visibility as IPostVisibility}
                    />
                  ) : (
                    <div />
                  )}
                </div>
                <p className="text-sm font-secondary capitalize">
                  {category?.name || ''}
                </p>
              </div>
              <p className="text-sm line-clamp-2 font-tertiary font-normal">
                {description}
              </p>
            </div>
          </div>
        </Link>

        <div className="flex justify-around pb-5">
          <LikeButton likes={likesCount} liked={hasLikedByUser} postId={id} />
          <Button variant="outline" size="sm">
            <div className="flex items-center gap-1 justify-center text-[13px]">
              <BiCommentDetail size="13px" />
              <p>Comment</p>
              <p>{commentsCount}</p>
            </div>
          </Button>
          <Button variant="outline" size="sm">
            <div className="flex items-center gap-1 justify-center text-[13px]">
              <Download size="13px" />
              <p>Downloads</p>
              <p>{totalDownloadCount}</p>
            </div>
          </Button>
        </div>
      </Card>

      {isDeleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this project? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PostCard;
